import React from 'react';
import { ReactComponent as Clipboard } from 'assets/icons/ui-elements/small/clipboard-sm.svg';
import { ReactComponent as User } from 'assets/icons/ui-elements/small/user-sm.svg';
import { ReactComponent as House } from 'assets/icons/ui-elements/small/house-sm.svg';
import { ReactComponent as Building } from 'assets/icons/building.svg';
import { ReactComponent as EyeOn } from 'assets/icons/ui-elements/small/eye-on-sm.svg';
import { ReactComponent as DownIcon } from 'assets/icons/ui-elements/small/caret-down-sm.svg';
import {
  FiltersContainer,
  TypeBlock,
  TypeFilterContainer,
  TypeButton,
  OpenModalButton,
  FiltersGridWrapper,
  FilterCell,
  FiltersGridColumn,
  UnidadesFieldWrapper,
} from '../styled';
import { MultiSelectDropdown } from 'newComponents/Form/Multiselect';
import { EventType, DisplayMode } from '../../../hooks/useCalendarFilters';
import { FittingFilterButtonLabel } from './FittingFilterButtonLabel';

const DISPLAY_MODE_OPTIONS = [
  {
    label: 'Exibir na agenda:',
    items: [
      { label: 'Atividades', value: 'atividades' },
      { label: 'Ocorrências', value: 'ocorrencias' },
    ],
  },
];

function displayModeToValues(mode: DisplayMode): string[] {
  if (mode === 'ambos') return ['atividades', 'ocorrencias'];
  return [mode];
}

function valuesToDisplayMode(values: string[]): DisplayMode {
  const hasA = values.includes('atividades');
  const hasO = values.includes('ocorrencias');
  if (hasA && !hasO) return 'atividades';
  if (hasO && !hasA) return 'ocorrencias';
  return 'ambos';
}

interface CalendarFiltersProps {
  type: EventType;
  onTypeChange: (type: EventType) => void;
  onOpenExecutoresModal: () => void;
  onOpenAmbientesModal: () => void;
  onOpenPlanosModal: () => void;
  planosSelectionLabel: string;
  executoresSetoresSelectionLabel: string;
  ambientesEquipamentosSelectionLabel: string;
  maintenanceUnitsOptions: { label: string; value: string }[];
  maintenanceUnitsValues: string[];
  onMaintenanceUnitsChange: (values: unknown) => void;
  displayMode?: DisplayMode;
  onDisplayModeChange?: (mode: DisplayMode) => void;
  canViewOccurrences?: boolean;
  /** When true, hide Pendentes/Baixadas tabs (e.g. on mobile where they are in the toolbar). */
  hideTypeTabs?: boolean;
}

export const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  type,
  onTypeChange,
  onOpenExecutoresModal,
  onOpenAmbientesModal,
  onOpenPlanosModal,
  planosSelectionLabel,
  executoresSetoresSelectionLabel,
  ambientesEquipamentosSelectionLabel,
  maintenanceUnitsOptions,
  maintenanceUnitsValues,
  onMaintenanceUnitsChange,
  displayMode = 'ambos',
  onDisplayModeChange,
  canViewOccurrences = false,
  hideTypeTabs = false,
}) => {
  return (
    <FiltersContainer>
      {!hideTypeTabs && (
        <TypeBlock>
          <TypeFilterContainer>
            <TypeButton isActive={type === 'pendentes'} onClick={() => onTypeChange('pendentes')} isRight={false}>
              Pendentes
            </TypeButton>
            <TypeButton isActive={type === 'baixadas'} onClick={() => onTypeChange('baixadas')} isRight={true}>
              Baixadas
            </TypeButton>
          </TypeFilterContainer>
          {canViewOccurrences && (
            <UnidadesFieldWrapper>
              <MultiSelectDropdown
                options={DISPLAY_MODE_OPTIONS}
                value={displayModeToValues(displayMode)}
                onChange={values => onDisplayModeChange?.(valuesToDisplayMode(values))}
                size="sm"
                width="100%"
                iconLeft={[<EyeOn key="eye-on-icon" />]}
                temCategorias
                allSelectedLabel="Atividades e ocorrências"
              />
            </UnidadesFieldWrapper>
          )}
        </TypeBlock>
      )}
      <FiltersGridWrapper>
        <FiltersGridColumn>
          <FilterCell>
            <UnidadesFieldWrapper>
              <MultiSelectDropdown
                options={maintenanceUnitsOptions}
                value={maintenanceUnitsValues}
                onChange={values => onMaintenanceUnitsChange(values as string[])}
                size="sm"
                width="100%"
                placeholder="Unidades"
                clearable
                searchable
                iconLeft={[<Building key="building-icon" />]}
                grayLabel
              />
            </UnidadesFieldWrapper>
          </FilterCell>

          <FilterCell>
            <OpenModalButton onClick={onOpenExecutoresModal} width="100%" size="small">
              <FittingFilterButtonLabel
                icon={<User />}
                text={`Executores e Setores${executoresSetoresSelectionLabel}`}
              />
              <DownIcon />
            </OpenModalButton>
          </FilterCell>
        </FiltersGridColumn>

        <FiltersGridColumn>
          <FilterCell>
            <OpenModalButton onClick={onOpenPlanosModal} width="100%" size="small">
              <FittingFilterButtonLabel icon={<Clipboard />} text={`Planos${planosSelectionLabel}`} />
              <DownIcon />
            </OpenModalButton>
          </FilterCell>
          <FilterCell>
            <OpenModalButton onClick={onOpenAmbientesModal} width="100%" size="small">
              <FittingFilterButtonLabel
                icon={<House />}
                text={`Ambientes e Equipamentos${ambientesEquipamentosSelectionLabel}`}
              />
              <DownIcon />
            </OpenModalButton>
          </FilterCell>
        </FiltersGridColumn>
      </FiltersGridWrapper>
    </FiltersContainer>
  );
};
